import axios from "axios";
import { resolveBaseUrlFor } from "../utils/backendConfig";
import {
  getTokensFromStorage,
  acquireRefreshLock,
  releaseRefreshLock,
} from "./tokenChannel";

let failedQueue = [];
let isRefreshing = false;

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

export function createAxiosClient({
  options,
  getCurrentAccessToken,
  getCurrentRefreshToken,
  refreshTokenUrl,
  logout,
  setRefreshedTokens,
  onImplementationMismatch,
  onPermsStampChange,
  onPermissionDenied,
}) {
  const client = axios.create(options);

  client.interceptors.request.use(
    (config) => {
      // Modo hibrido de debug (``useProductionForStatusReads``): se a
      // request casa com a regra de redirecionamento (GET/HEAD em rota
      // de inputs V2/V3, com backend principal local), sobrescreve o
      // baseURL para producao. Caso contrario, mantem o default da
      // instancia do axios (= ``baseUrl`` global).
      const overrideBaseUrl = resolveBaseUrlFor(config.url, config.method);
      if (overrideBaseUrl) {
        config.baseURL = overrideBaseUrl;
      }

      if (config.authorization !== false) {
        const token = getCurrentAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  client.interceptors.response.use(
    (response) => {
      if (onImplementationMismatch) {
        const serverImplementation =
          response.headers["x-current-implementation"];
        if (serverImplementation) {
          onImplementationMismatch(serverImplementation);
        }
      }
      if (onPermsStampChange) {
        const serverStamp = response.headers["x-perms-stamp"];
        if (serverStamp) {
          onPermsStampChange(serverStamp);
        }
      }
      return response;
    },
    (error) => {
      // Leva 100: detecção de drift de implementação também em
      // RESPOSTAS DE ERRO. O middleware backend
      // ``UserImplementationHeaderMiddleware`` injeta o header
      // ``X-Current-Implementation`` em **qualquer** response — sucesso
      // ou erro — desde que o user esteja autenticado e tenha uma
      // ``current_implementation`` válida. Antes só o caminho de
      // sucesso (acima) inspecionava o header; resultado: views como
      // ``InputsV3`` que sempre falham com 403
      // ``implementation_mismatch`` (porque carregam ``<app_name>``
      // defasado no path) nunca disparavam o redirect para
      // ``/welcome``, ficando travadas em polling de erro. Agora o
      // caminho de erro também aciona o ``handleImplementationMismatch``
      // (Redux + redirect) — único, central, beneficia todas as views.
      //
      // ``handleImplementationMismatch`` compara
      // ``serverImplementation !== localImplementation`` antes de agir;
      // se baterem (caso de 4xx sem drift, ex.: erro de validação
      // normal), o handler é no-op. Seguro disparar a cada erro.
      if (onImplementationMismatch && error.response?.headers) {
        const serverImplementation =
          error.response.headers["x-current-implementation"];
        if (serverImplementation) {
          onImplementationMismatch(serverImplementation);
        }
      }

      if (onPermsStampChange && error.response?.headers) {
        const serverStamp = error.response.headers["x-perms-stamp"];
        if (serverStamp) {
          onPermsStampChange(serverStamp);
        }
      }

      const originalRequest = error.config;
      // In "axios": "^1.1.3" there is an issue with headers, and this is the workaround.
      originalRequest.headers = JSON.parse(
        JSON.stringify(originalRequest.headers || {}),
      );
      const refreshToken = getCurrentRefreshToken();

      const handleError = (error) => {
        processQueue(error);
        // Add a flag to indicate this was a logout due to token expiration
        error.isTokenExpirationLogout = true;
        logout();
        return Promise.reject(error);
      };

      if (
        refreshToken &&
        error.response?.status === 401 &&
        error.response?.data?.code === "token_not_valid" &&
        originalRequest?.url !== refreshTokenUrl &&
        originalRequest?._retry !== true
      ) {
        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          })
            .then(() => {
              return client(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }
        isRefreshing = true;
        originalRequest._retry = true;
        return acquireRefreshLock()
          .then(({ acquired }) => {
            if (!acquired) {
              const storedTokens = getTokensFromStorage();
              if (storedTokens?.access) {
                setRefreshedTokens(storedTokens);
                processQueue(null);
                return client(originalRequest);
              }
              return handleError(
                new Error("No tokens after cross-tab refresh"),
              );
            }

            return client
              .post(refreshTokenUrl, {
                refresh: refreshToken,
              })
              .then(
                (res) => {
                  const tokens = {
                    access: res.data?.access,
                    refresh: res.data?.refresh,
                  };
                  setRefreshedTokens(tokens);
                  processQueue(null);
                  return client(originalRequest);
                },
                (refreshError) => {
                  const storedTokens = getTokensFromStorage();
                  const currentAccess = getCurrentAccessToken();
                  if (
                    storedTokens?.access &&
                    storedTokens.access !== currentAccess
                  ) {
                    setRefreshedTokens(storedTokens);
                    processQueue(null);
                    return client(originalRequest);
                  }
                  return handleError(refreshError);
                },
              )
              .finally(() => {
                releaseRefreshLock();
              });
          })
          .finally(() => {
            isRefreshing = false;
          });
      }

      if (originalRequest.url === "/token/") {
        return Promise.reject(error);
      }

      if (
        error.response?.status === 401 &&
        error.response?.data?.code === "token_not_valid" &&
        originalRequest?.url !== refreshTokenUrl
      ) {
        return handleError(error);
      }

      // Negacao de permissao (RolesV2): o backend responde 403 com
      // ``error_code`` no formato ``not_allowed_<atomo>`` quando o usuario
      // tenta uma ACAO sem permissao. Avisamos de forma amigavel (toast),
      // em vez de deixar o erro cru vazar para a tela. Nao confundir com o
      // 403 de ``implementation_mismatch`` (tratado via header acima) nem
      // com restricao de DADOS (escopo), que apenas filtra a query e nunca
      // retorna 403. Telas que ja mostram aviso proprio podem optar por
      // ``config.skipPermissionToast = true``.
      if (
        onPermissionDenied &&
        error.response?.status === 403 &&
        !originalRequest?.skipPermissionToast &&
        typeof error.response?.data?.error_code === "string" &&
        error.response.data.error_code.startsWith("not_allowed_")
      ) {
        onPermissionDenied(error);
      }

      return Promise.reject(error);
    },
  );

  return client;
}
